import {useDispatch, useSelector} from "react-redux"
import {useEffect, useState} from "react"
import {clearDeleteArticleError, deleteArticle, selectDeleteArticleError} from "../../../reducers/deletedArticlesSlice"
import {useSnackbar} from "notistack"
import axios from "axios"
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Menu} from "@mui/material"
import MenuItem from "@mui/material/MenuItem"
import {useWorkspaceContext} from "../../../pages/Dashboard"

export function ArticleSubmenu({fullText, setFullTextId, setArticleFiles, fromAutoSearch, articleWorkspace, anchorElRef, openSubMenu, setOpenSubMenu}) {
	const dispatch = useDispatch()
	const {workspaceId} = useWorkspaceContext()
	const [isAttemptingToRemoveArticle, setIsAttemptingToRemoveArticle] = useState(false)
	const [isAttemptingToRemoveFullText, setIsAttemptingToRemoveFullText] = useState(false)
	const {enqueueSnackbar} = useSnackbar()
	const deleteArticleError = useSelector((state) => selectDeleteArticleError(state, workspaceId))

	useEffect(() => {
		let timer
		if (deleteArticleError) {
			enqueueSnackbar("Failed to delete this article from all notes in workspace", {variant: "error"})
			timer = setTimeout(() => {
				dispatch(clearDeleteArticleError(workspaceId))
			}, 3000)
		}
		return () => {
			if (timer) {
				clearTimeout(timer)
			}
		}
	}, [deleteArticleError])


	const removeArticleFromWorkspace = () => {
		// pass params fromAutoSearch and articlePMID to remove article from auto list to handle paging logic in automated search panel
		dispatch(deleteArticle({
			articleId: articleWorkspace?.article?.id,
			fromAutoSearch,
			articlePMID: articleWorkspace?.article?.PMID,
			workspaceId
		}))
	}

	const removeFullTextFromArticle = () => {
		axios
			.delete(
				`/api/workspace/${workspaceId}/article/${articleWorkspace?.article?.id}/fulltext`
			)
			.then(({data}) => {
				setFullTextId(null)
				setArticleFiles(data.files)
				enqueueSnackbar("Deleted full-text PDF from article", {variant: "success"})
			})
			.catch(e => {
				console.error("ERROR ATTEMPTING TO REMOVE FULL-TEXT PDF FROM ARTICLE", e)
				enqueueSnackbar("Failed to remove the full-text PDF from this article", {variant: "error"})
			})
	}
	return (
		<>
			<Menu
				open={openSubMenu}
				onClose={() => setOpenSubMenu(false)}
				elevation={0}
				anchorEl={anchorElRef.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "center",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				MenuListProps={{
					dense: true,
					sx: {
						backgroundColor: "primary.transparency50",
						paddingTop: 0,
						paddingBottom: 0,
					}
				}}
				PaperProps={{
					sx: {
						borderRadius: "4px 0px 0px 4px"
					}
				}}
				TransitionProps={{
					style: {
						transformOrigin: "-1px 0 0",
						borderRadius: "4px 0px 0px 4px"
					}
				}}
			>
				{fullText &&
					<MenuItem
						onClick={() => setIsAttemptingToRemoveFullText(true)}
						dense
						divider
						sx={{
							justifyContent: "end"
						}}
					>Remove full-text PDF</MenuItem>
				}
				<MenuItem
					dense
					onClick={() => setIsAttemptingToRemoveArticle(true)}
					sx={{
						justifyContent: "end"
					}}
				>Delete article from workspace</MenuItem>
			</Menu>
			<ConfirmationModal
				isAttemptingToRemoveArticle={isAttemptingToRemoveArticle}
				setIsAttemptingToRemoveArticle={setIsAttemptingToRemoveArticle}
				isAttemptingToRemoveFullText={isAttemptingToRemoveFullText}
				setIsAttemptingToRemoveFullText={setIsAttemptingToRemoveFullText}
				removeArticleFromWorkspace={removeArticleFromWorkspace}
				removeFullText={removeFullTextFromArticle}
			/>
		</>
	)
}

function ConfirmationModal(
	{
		isAttemptingToRemoveArticle,
		setIsAttemptingToRemoveArticle,
		isAttemptingToRemoveFullText,
		setIsAttemptingToRemoveFullText,
		removeArticleFromWorkspace,
		removeFullText
	}
){
	const closeModal = () => {
		if(isAttemptingToRemoveArticle){
			setIsAttemptingToRemoveArticle(false)
		}

		if(isAttemptingToRemoveFullText){
			setIsAttemptingToRemoveFullText(false)
		}
	}

	const removeArticle = () => {
		removeArticleFromWorkspace()
		closeModal()
	}

	const removeFullTextFromArticle = () => {
		removeFullText()
		closeModal()
	}
	return (
		<Dialog
			open={
				isAttemptingToRemoveArticle === true
				|| isAttemptingToRemoveFullText === true
			}
			onClose={closeModal}
		>
			{ isAttemptingToRemoveArticle &&
				<RemoveFromWorkspaceDialog
					removeArticle={removeArticle}
					closeModal={closeModal}
				/>
			}
			{ isAttemptingToRemoveFullText &&
				<RemoveFullTextDialog
					removeFullTextFromArticle={removeFullTextFromArticle}
					closeModal={closeModal}
				/>
			}
		</Dialog>
	)
}

function RemoveFromWorkspaceDialog({removeArticle, closeModal}) {
	return (
		<>
			<DialogTitle>
				Are you sure you want to remove this article from the Workspace?
			</DialogTitle>
			<DialogContent>
				Removing this article from the Workspace will detach it from ALL notes in workspace. The article can be re-imported into the workspace, but the current relations to Workspace notes will be lost.
			</DialogContent>
			<DialogActions>
				<Button
					onClick={removeArticle}
					variant={"outlined"}
				>Remove Article From Workspace</Button>
				<Button
					onClick={closeModal}
					variant={"outlined"}
					color={"error"}
				>Cancel</Button>
			</DialogActions>
		</>
	)
}

function RemoveFullTextDialog({removeFullTextFromArticle, closeModal}) {
	return (
		<>
			<DialogTitle>
				Are you sure you want to remove the full-text PDF from this Article?
			</DialogTitle>
			<DialogContent>
				Removing the full-text PDF from this article will prevent you from reading and annotating the PDF in this workspace.
				Instead, it will convert the current full-text PDF into an attachment, which you can then choose to delete from RefZoom. The attachment can still be downloaded, but cannot be read or annotated on inside of RefZoom.
				You can set a new full-text PDF by uploading a new file.
			</DialogContent>
			<DialogActions>
				<Button
					onClick={removeFullTextFromArticle}
					variant={"outlined"}
				>Remove the full-text PDF from this Article</Button>
				<Button
					onClick={closeModal}
					variant={"outlined"}
					color={"error"}
				>Cancel</Button>
			</DialogActions>
		</>
	)
}